import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, OnDestroy, Output } from '@angular/core';
import { GameOverlayService } from '../../../../../services/game-overlay/game-overlay.service';
import { isMobile } from '../../../../../classes/common/helpers';
import { GameUiService } from '../../../../../services/game-ui/game-ui.service';
import { debounceTime, finalize, interval, Subscription, takeWhile } from 'rxjs';
import { ResultModalWithActionsComponent } from '../../../../ui/base/result-modal-with-actions/result-modal-with-actions.component';
import { BackgropComponent } from '../../../../ui/base/backgrop/backgrop.component';
import { IconComponent, IconName } from '../../../../ui/base/icon/icon.component';
import { AudioService } from '../../../../../services/audio/audio.service';
import { CenterTextParam } from '../../../../ui/base/center-text/center-text.component';

@Component({
  selector: 'app-match-three',
  templateUrl: './match-three.component.html',
  styleUrl: './match-three.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchThreeComponent implements AfterViewInit, OnDestroy {
  @Output() closed: EventEmitter<void> = new EventEmitter();

  board: any[][] = [];

  iconName = IconName;
  colors: string[] = [];
  mobileColors = ['1', '2', '3', '4', '5', '6', '7'];
  desktopColors = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  // boardSize = cols, boardSize2 = rows
  boardSize: number;    // columns
  boardSize2: number;   // rows

  loadedIconCount = 0;
  selectedColsCoordinates: any[] = [];

  totalMatches = 10;
  currentMatches = 0;

  showResult = false;
  pause = false;
  showHint = false;
  showGameContainer = false;

  skipSubscription: Subscription;

  hintCount = 3;
  timeLeft = 59;
  isFail = false;
  private sub?: Subscription;

  showCenterText = false;
  
  showCenterTextParam: CenterTextParam[] = [
    {
      text: 'Старт',
      duration: 1000,
    },
  ];
  completedFn;
  constructor(
    private cdr: ChangeDetectorRef,
    private sound: AudioService,
    private ui: GameUiService,
    private overlay: GameOverlayService
  ) {
    
    this.skipSubscription = this.ui.skipClicked$.pipe(debounceTime(300)).subscribe(click => {
      if (click) {
        this.close();
      }
    });

  }

  ngAfterViewInit(): void {
    this.boardSize = isMobile() ? 5 : 8;    // columns
    this.boardSize2 = isMobile() ? 5 : 7;   // rows
    this.colors = isMobile() ? this.mobileColors : this.desktopColors;
    this.createBoard();
    this.showGame();
    this.overlay.hideSpeaker();
    this.cdr.detectChanges();

    this.showCenterTextParam = [
      {
        text: 'Старт',
        duration: 1000,
      },
    ];
    this.completedFn = () => {
      this.showCenterText = false;
      this.cdr.detectChanges();
      this.startTimer();
    };
    setTimeout(() => {this.showCenterText = true; this.cdr.detectChanges();}, 200);
  }

  ngOnDestroy(): void {
    this.skipSubscription?.unsubscribe();
    this.stopTimer();
  }

  playHover() {
    this.sound.playSfx('match_three_active_cell');
  }
  playClick() {
    this.sound.playSfx('match_three_сell_selection');
  }
  playChange() {
    this.sound.playSfx('match_three_сell_move');
  }
  playCorrect() {
    this.sound.playSfx('match_three_correct');
  }
  playNew() {
    this.sound.playSfx('match_three_cells_drop');
  }
  playIncorrect() {
    this.sound.playSfx('match_three_сell_wrong');
  }

  startTimer() {
    this.stopTimer();

    this.timeLeft = 59;
    this.sub = interval(1000).pipe(
      takeWhile(() => this.timeLeft > 0),
      finalize(() => {
        if (this.timeLeft === 0) this.gameOver();
      })
    ).subscribe(() => {
      if (!this.ui.hintVisible) {
        this.timeLeft--;
        this.cdr.detectChanges();
      }
    });
  }

  stopTimer() {
    this.sub?.unsubscribe();
  }

  gameOver() {
    this.pause = true;
    this.isFail = true;
    this.showResult = true;
    this.cdr.detectChanges();
  }

  showGame() {
    this.showGameContainer = true;
    setTimeout(() => {
      this.checkField();
      this.cdr.detectChanges();
    }, 1000);
    this.cdr.detectChanges();
  }

  createBoard() {
    this.board = [];
    for (let r = 0; r < this.boardSize2; r++) {
      this.board[r] = [];
      for (let c = 0; c < this.boardSize; c++) {
        this.board[r][c] = {
          icon: this.colors[Math.floor(Math.random() * this.colors.length)],
          isSelect: false,
          hint: false,
          isRemoved: false,
          created: true,
        };
      }
    }
  }

  checkField() {
    const matches = this.checkMatches();
    this.pause = true;

    if (matches.length) {
      setTimeout(() => {
        this.removeMatches(matches);
        this.playCorrect();
        this.cdr.detectChanges();
      }, 300);

      setTimeout(() => {
        this.playNew();
        this.updateBoard();
        this.cdr.detectChanges();
        this.checkField();
      }, 1000);
    } else {
      const hasMove = this.hasValidMove(this.board);
      this.cdr.detectChanges();
      if (!hasMove) {
        this.completedFn = () => {
          this.showCenterText = false;
          this.createBoard();
          this.checkField();
        };
        this.showCenterTextParam = [
          {
            text: 'Нет ходов.',
            duration: 300,
          },{
            text: 'Перемешиваем...',
            duration: 700,
          },
        ];
        this.showCenterText = true;
      } else {
        this.pause = false;
      }
    }
  }

  /**
   * Возвращает массив уникальных координат матчей: [[row,col], ...]
   * Принимает optional board — если нужно проверить временную доску
   */
  checkMatches(boardParam?: any[][]) {
    const board = boardParam ?? this.board;
    const coordsSet = new Set<string>();
    const matches: number[][] = [];

    // Горизонтальные (строки)
    for (let row = 0; row < this.boardSize2; row++) {
      for (let col = 0; col < this.boardSize - 2; col++) {
        const a = board[row][col];
        const b = board[row][col + 1];
        const c = board[row][col + 2];
        if (!a || !b || !c) continue;
        if (a.icon === b.icon && a.icon === c.icon) {
          [[row, col], [row, col + 1], [row, col + 2]].forEach(([r, co]) => {
            const key = `${r},${co}`;
            if (!coordsSet.has(key)) {
              coordsSet.add(key);
              matches.push([r, co]);
            }
          });
        }
      }
    }

    // Вертикальные (столбцы)
    for (let col = 0; col < this.boardSize; col++) {
      for (let row = 0; row < this.boardSize2 - 2; row++) {
        const a = board[row][col];
        const b = board[row + 1][col];
        const c = board[row + 2][col];
        if (!a || !b || !c) continue;
        if (a.icon === b.icon && a.icon === c.icon) {
          [[row, col], [row + 1, col], [row + 2, col]].forEach(([r, co]) => {
            const key = `${r},${co}`;
            if (!coordsSet.has(key)) {
              coordsSet.add(key);
              matches.push([r, co]);
            }
          });
        }
      }
    }

    return matches;
  }

  removeMatches(matches: number[][]) {
    matches.forEach(([row, col]) => {
      if (this.board?.[row]?.[col]) {
        this.board[row][col].isRemoved = true;
        setTimeout(() => {
          this.board[row][col] = null;
        }, 300);
      }
    });

    // Сбрасываем флаг created для существующих ячеек
    for (let row = 0; row < this.boardSize2; row++) {
      for (let col = 0; col < this.boardSize; col++) {
        if (this.board[row][col]) this.board[row][col].created = false;
      }
    }
  }

  updateBoard() {
    // Сдвигаем вниз по колонкам (rows: boardSize2)
    for (let col = 0; col < this.boardSize; col++) {
      for (let row = this.boardSize2 - 1; row >= 0; row--) {
        if (this.board[row][col] === null) {
          for (let r = row - 1; r >= 0; r--) {
            if (this.board[r][col] !== null) {
              this.board[row][col] = this.board[r][col];
              this.board[r][col] = null;
              break;
            }
          }
        }
      }
    }

    // Заполнение сверху новыми элементами
    for (let col = 0; col < this.boardSize; col++) {
      for (let row = 0; row < this.boardSize2; row++) {
        if (this.board[row][col] === null) {
          const color = this.colors[Math.floor(Math.random() * this.colors.length)];
          this.board[row][col] = {
            icon: color,
            isSelect: false,
            hint: false,
            isRemoved: false,
            created: true
          };
        }
      }
    }
  }

  loadedIcon() {
    this.loadedIconCount++;
  }

  hint() {
    if (!this.hintCount || this.showHint) return;
    this.sound.playClickSmallBtn();
    this.showHint = true;
    this.hintCount--;
    this.cdr.detectChanges();
  }

  startWatch(event: PointerEvent, x: number, y: number, col) {
    // x = row, y = col
    this.selectCol(x, y, col);

    const startX = event.clientX;
    const startY = event.clientY;

    const onMove = (e: PointerEvent) => {
      e.stopImmediatePropagation();
      e.stopPropagation();
      e.preventDefault();
      const dy = e.clientX - startX;
      const dx = e.clientY - startY;

      if (Math.abs(dx) > 20 || Math.abs(dy) > 20) {
        let newX = x;
        let newY = y;

        if (Math.abs(dx) > Math.abs(dy)) {
          // Горизонтальное движение (по X экрана -> меняется колонка)
          newX = dx > 0 ? x + 1 : x - 1;
        } else {
          // Вертикальное движение
          newY = dy > 0 ? y + 1 : y - 1;
        }

        // Проверяем границы и наличие ячейки
        if (this.board?.[newX]?.[newY]) {
          const nextCol = this.board[newX][newY];
          this.selectCol(newX, newY, nextCol);
        }

        cleanup();
      }
    };

    const onUp = () => cleanup();

    const cleanup = () => {
      document.getElementById('board')!.removeEventListener('pointermove', onMove);
      document.getElementById('board')!.removeEventListener('pointerup', onUp);
    };

    document.getElementById('board')!.addEventListener('pointermove', onMove);
    document.getElementById('board')!.addEventListener('pointerup', onUp);
  }

  selectCol(x: number, y: number, col) {
    if (this.pause) return;

    this.showHint = false;
    col.isSelect = true;
    if (!this.selectedColsCoordinates.length) {
      this.selectedColsCoordinates.push([[x, y], col]);
      this.playClick();
    } else if (this.selectedColsCoordinates.length !== 2) {
      if (this.checkIsPossibleChange(this.selectedColsCoordinates[0][0], [x, y])) {
        this.selectedColsCoordinates.push([[x, y], col]);
        this.changeCols();
      } else {
        this.selectedColsCoordinates[0][1].isSelect = false;
        this.selectedColsCoordinates[0] = [[x, y], col];
        col.isSelect = true;
      }
    }
    this.cdr.detectChanges();
  }

  checkIsPossibleChange(col1: number[], col2: number[]) {
    const diffX = Math.abs(col1[0] - col2[0]);
    const diffY = Math.abs(col1[1] - col2[1]);
    return (diffX === 1 && diffY === 0) || (diffX === 0 && diffY === 1);
  }

  changeCols(isReturn = false) {
    this.playChange();
    const col1 = this.selectedColsCoordinates[0][0];
    const col2 = this.selectedColsCoordinates[1][0];

    // swap в this.board
    [this.board[col1[0]][col1[1]], this.board[col2[0]][col2[1]]] = [this.board[col2[0]][col2[1]], this.board[col1[0]][col1[1]]];

    this.board[col1[0]][col1[1]].isSelect = this.board[col2[0]][col2[1]].isSelect = false;
    this.board[col1[0]][col1[1]].created = this.board[col2[0]][col2[1]].created = false;

    if (!isReturn && this.checkMatches().length) {
      this.resetHint();
      this.checkField();
      this.selectedColsCoordinates.length = 0;
      this.currentMatches++;
      if (this.currentMatches === this.totalMatches) {
        setTimeout(() => {
          this.showResult = true;
          this.stopTimer();
          this.cdr.detectChanges();
        }, 500);
      }
    } else if (!isReturn) {
      setTimeout(() => {
        this.changeCols(true);
        this.playIncorrect();
      }, 500);
    } else {
      this.selectedColsCoordinates.length = 0;
    }
    this.cdr.detectChanges();
  }

  hasValidMove(board: any[][]) {
    // Перебираем координаты на board: rows = boardSize2, cols = boardSize
    for (let row = 0; row < this.boardSize2; row++) {
      for (let col = 0; col < this.boardSize; col++) {
        // Проверяем правый swap (col + 1)
        if (col + 1 < this.boardSize) {
          this.swap(board, row, col, row, col + 1);
          if (this.checkMatches(board).length) {
            this.swap(board, row, col, row, col + 1); // отмена
            board[row][col].hint = true;
            board[row][col + 1].hint = true;
            return true;
          }
          this.swap(board, row, col, row, col + 1); // отмена
        }

        // Проверяем вниз (row + 1)
        if (row + 1 < this.boardSize2) {
          this.swap(board, row, col, row + 1, col);
          if (this.checkMatches(board).length) {
            this.swap(board, row, col, row + 1, col); // отмена
            board[row][col].hint = true;
            board[row + 1][col].hint = true;
            return true;
          }
          this.swap(board, row, col, row + 1, col); // отмена
        }
      }
    }
    return false;
  }

  swap(board, row1, col1, row2, col2) {
    const temp = board[row1][col1];
    board[row1][col1] = board[row2][col2];
    board[row2][col2] = temp;
  }

  resetHint() {
    for (let r = 0; r < this.boardSize2; r++) {
      for (let c = 0; c < this.boardSize; c++) {
        if (this.board[r][c]) this.board[r][c].hint = false;
      }
    }
  }

  reset() {
    this.currentMatches = 0;
    this.hintCount = 3;
    this.showResult = false;
    this.isFail = false;
    this.createBoard();
    this.checkField();
    
    this.showCenterTextParam = [
      {
        text: 'Старт',
        duration: 1000,
      },
    ];
    this.completedFn = () => {
      this.showCenterText = false;
      this.cdr.detectChanges();
      this.startTimer();
    };
    setTimeout(() => {this.showCenterText = true; this.cdr.detectChanges();}, 200);
  }

  close() {
    this.closed.emit();
  }
}
